/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState } from 'react';
import { GitPullRequestArrow, Play, ShieldAlert } from 'lucide-react';
import { chamarApi } from '@/nucleo/auth/cliente-api';
import { Alert, AlertDescription } from '@/ui/base/alert';
import { Button } from '@/ui/base/button';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';
import { EmptyState } from '@/ui/EmptyState';
import { PageHeader } from '@/ui/PageHeader';

const NS = '/bff/v1/j13-s02-mapear-transformar';
type Env = { state: any };
type Mapa = { origem: string; destino?: string; regra: string; motivo?: string };
export function MapearTransformarPage() {
  const [e, se] = useState<any>(),
    [erro, setErro] = useState(''),
    [busy, setBusy] = useState(false),
    [inventario, setInventario] = useState(''),
    [modelo, setModelo] = useState(''),
    [mapa, setMapa] = useState<Mapa[]>([]),
    [justificativa, setJustificativa] = useState('');
  const chave = useRef(crypto.randomUUID());
  useEffect(() => {
    void chamarApi<Env>(`${NS}/state`)
      .then((x) => {
        se(x.state);
        const i = x.state.inventarios[0],
          m = x.state.modelos[0];
        if (i) {
          setInventario(i.id);
          setMapa(i.campos.map((origem: string) => ({ origem, destino: '', regra: 'COPIAR' })));
        }
        if (m) setModelo(m.id);
      })
      .catch(() => setErro('Não foi possível carregar o mapeamento.'));
  }, []);
  async function verbo(nome: string, corpo: unknown) {
    setBusy(true);
    setErro('');
    try {
      const x = await chamarApi<Env>(`${NS}/verbs/${nome}`, { metodo: 'POST', corpo });
      se(x.state);
      return x.state;
    } catch {
      setErro('Não foi possível concluir. Revise o mapa e tente novamente.');
    } finally {
      setBusy(false);
    }
  }
  function trocarInventario(id: string) {
    setInventario(id);
    const i = e.inventarios.find((x: any) => x.id === id);
    setMapa(i.campos.map((origem: string) => ({ origem, destino: '', regra: 'COPIAR' })));
    chave.current = crypto.randomUUID();
  }
  function mudar(i: number, campo: keyof Mapa, valor: string) {
    setMapa((atual) => atual.map((x, n) => (n === i ? { ...x, [campo]: valor } : x)));
    chave.current = crypto.randomUUID();
  }
  if (!e)
    return (
      <EmptyState
        variant={erro ? 'error' : 'default'}
        title={erro || 'Carregando mapeamento…'}
        description={erro ? 'Tente novamente em instantes.' : 'Aguarde.'}
      />
    );
  const modeloAtual = e.modelos.find((x: any) => x.id === modelo),
    det = e.detalhe;
  return (
    <section className="min-w-0 max-w-[calc(100vw-2rem)] space-y-5 overflow-hidden">
      <PageHeader title={e.titulo} description={e.descricao} />
      {[...e.alertas.map((a: any) => a.mensagem), ...(erro ? [erro] : [])].map((m: string) => (
        <Alert key={m}>
          <AlertDescription>{m}</AlertDescription>
        </Alert>
      ))}
      <article className="space-y-4 rounded-md border p-4">
        <h2 className="font-semibold">Mapa de correspondências</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="inventario">Inventário aprovado</Label>
            <select
              id="inventario"
              className="h-10 w-full rounded-md border bg-background px-3"
              value={inventario}
              onChange={(x) => trocarInventario(x.target.value)}
            >
              {e.inventarios.map((i: any) => (
                <option value={i.id} key={i.id}>
                  Versão {i.numero} · {i.sistema}/{i.tabela_origem}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="modelo">Modelo-alvo versionado</Label>
            <select
              id="modelo"
              className="h-10 w-full rounded-md border bg-background px-3"
              value={modelo}
              onChange={(x) => {
                setModelo(x.target.value);
                chave.current = crypto.randomUUID();
              }}
            >
              {e.modelos.map((m: any) => (
                <option value={m.id} key={m.id}>
                  {m.codigo} v{m.versao}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="grid gap-3">
          {mapa.map((m, i) => (
            <div className="grid gap-2 rounded-md border p-3 sm:grid-cols-3" key={m.origem}>
              <p className="self-center font-mono text-sm">{m.origem}</p>
              <div>
                <Label htmlFor={`dest-${i}`}>Campo de destino</Label>
                <select
                  id={`dest-${i}`}
                  className="h-10 w-full rounded-md border bg-background px-2"
                  value={m.destino ?? ''}
                  onChange={(x) => mudar(i, 'destino', x.target.value)}
                >
                  <option value="">Sem equivalência</option>
                  {modeloAtual?.campos.map((c: any) => (
                    <option key={c.nome} value={c.nome}>
                      {c.nome}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor={`regra-${i}`}>Normalização</Label>
                <select
                  id={`regra-${i}`}
                  className="h-10 w-full rounded-md border bg-background px-2"
                  value={m.regra}
                  onChange={(x) => mudar(i, 'regra', x.target.value)}
                >
                  <option value="COPIAR">Copiar texto</option>
                  <option value="MAIUSCULAS">Maiúsculas</option>
                  <option value="COMPETENCIA">Competência AAAA-MM</option>
                  <option value="UUID">UUID</option>
                  <option value="INTEIRO">Inteiro</option>
                  <option value="DECIMAL">Decimal monetário</option>
                  <option value="REJEITAR">Rejeitar campo formalmente</option>
                </select>
              </div>
            </div>
          ))}
        </div>
        <Button
          disabled={busy || !inventario || !modelo}
          onClick={() =>
            void verbo('executar_transformacao', {
              inventarioId: inventario,
              modeloAlvoId: modelo,
              mapeamentos: mapa,
              chaveIdempotencia: chave.current,
            })
          }
        >
          <Play className="mr-2 h-4 w-4" />
          Executar transformação isolada
        </Button>
      </article>
      <section>
        <h2 className="mb-3 font-semibold">Lotes e repetibilidade</h2>
        {e.lotes.length === 0 ? (
          <EmptyState
            icon={GitPullRequestArrow}
            title="Nenhum lote executado"
            description="Conclua o mapa para produzir saída isolada e linhagem."
          />
        ) : (
          <div className="grid gap-3">
            {e.lotes.map((l: any) => (
              <button
                type="button"
                key={l.id}
                className="rounded-md border p-4 text-left focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => void verbo('selecionar_lote', { loteId: l.id })}
              >
                <strong>
                  {l.estado} · {l.registros_saida}/{l.registros_entrada} registro(s)
                </strong>
                <span className="mt-1 block break-all font-mono text-xs">
                  Entrada {l.entrada_hash}
                  <br />
                  Saída {l.saida_hash ?? 'não publicada'}
                </span>
              </button>
            ))}
          </div>
        )}
      </section>
      {det && (
        <aside className="space-y-3 rounded-md border p-4">
          <h2 className="font-semibold">Lote {det.id}</h2>
          <p className="text-sm">Código {det.codigo_transformacao}</p>
          <p className="break-all font-mono text-xs">
            Mapa {det.mapa_hash}
            <br />
            Código {det.codigo_hash}
          </p>
          {det.amostra.length > 0 && (
            <pre className="max-w-full overflow-auto rounded-md bg-muted p-3 text-xs">
              {JSON.stringify(det.amostra, null, 2)}
            </pre>
          )}
          {det.linhagem.map((l: any) => (
            <p className="break-all text-xs" key={l.id}>
              Linha {l.linha_origem} → {l.chave_saida} · {l.entrada_hash} → {l.saida_hash}
            </p>
          ))}
          {det.erros.map((x: any) => (
            <Alert key={x.id}>
              <ShieldAlert className="h-4 w-4" />
              <AlertDescription>
                {x.categoria}: {x.mensagem}
              </AlertDescription>
            </Alert>
          ))}
          {det.estado === 'BLOQUEADO' && (
            <div className="grid gap-2">
              <Label htmlFor="justificativa">Decisão formal do negócio</Label>
              <Input
                id="justificativa"
                value={justificativa}
                onChange={(x) => setJustificativa(x.target.value)}
              />
              <Button
                variant="outline"
                disabled={busy || justificativa.trim().length < 20}
                onClick={() =>
                  void verbo('registrar_decisao', {
                    loteId: det.id,
                    decisao: 'CORRIGIR_MAPA',
                    justificativa,
                    chaveIdempotencia: `decisao-${det.id}`,
                  })
                }
              >
                Registrar decisão de corrigir o mapa
              </Button>
            </div>
          )}
          {det.decisoes.map((d: any) => (
            <p className="text-sm" key={d.id}>
              Decisão {d.decisao}: {d.justificativa}
            </p>
          ))}
        </aside>
      )}
    </section>
  );
}
